import {inject, injectable} from "inversify";
import {Stadium} from "@/modules/stadium/entity";
import {Team} from "@/modules/team/entity";
import TerraceFactory from "@/modules/stadium/services/factories/TerraceFactory";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {FoundationDefinitionType} from "@/modules/stadium/types";
import {TerraceTypeEnum} from "@/modules/stadium/enums";
import chance from "@/modules/base/services/chance";

@injectable()
export default class StadiumFactory {
  // seats will be rounded to given values depending on the foundation level
  readonly steps = [
    100,
    250,
    500,
    1000,
    2500,
  ];

  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(TerraceFactory) private terraceFactory: TerraceFactory,
  ) {
  }

  public async generate(team: Team, foundationLevel = 1) {
    const definition: FoundationDefinitionType = this.configRepository.get('stadium.foundations')[foundationLevel - 1];

    const stadium = new Stadium();
    stadium.team = team;
    stadium.foundationLevel = foundationLevel;
    await stadium.save();

    await Object.values(TerraceTypeEnum)
      .forEachAsync(async (type: TerraceTypeEnum) => {
        const def = definition[type];

        const step = this.steps[foundationLevel - 1];
        const limit = def.limit ?? 0;
        let seats = limit ? chance.integer({min: 0, max: limit}) : 0;
        seats = Math.round(seats / step) * step;
        seats = Math.min(seats, limit);

        // not 0 seats if available + east block
        if (def.available && type === TerraceTypeEnum.BLOCK_EAST) {
          seats = Math.max(seats, step);
        }

        const terrace = await this.terraceFactory.generate({
          type,
          available: def.available,
          limit,
          seats,
          entranceFee: def.entranceFee ?? 0,
        });

        terrace.stadium = stadium;
        await terrace.save();
      });


    return stadium;
  }
}
